///
/// EarthBrowser
///
/// Created by Matt Giger on 4/10/2026
/// Copyright (c) 2026 Geodata Labs
///

import { AnchoredPanelShell, type PanelShellContext } from "@/components/controls/anchored-panel/AnchoredPanelShell"
import { MoreSection } from "@/components/controls/anchored-panel/panelAtoms"
import { Constants } from "@/constants"
import { useCurrentAirQuality, type AirQualityInfo } from "@/earthbrowser/dataset-distribution/air-quality"
import { useCurrentWeatherGrid, useDailyForecastGrid, useHourlyForecastGrid } from "@/earthbrowser/dataset-distribution/weather-grid"
import { convertPrecipitation, convertTemperature, formatPrecipitation, formatSpeed, getPrecipitationUnit } from "@/earthbrowser/utils/unitFormatters"
import { cFreeForecastDays, cPaidForecastDays, useEntitlements } from "@/hooks/useEntitlements"
import { setAuthOpen, setSubscribeOpen } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import type { CurrentWeatherResponse, DailyForecast, HourlyForecast } from "@/types/WeatherEntry"
import type { LocationInfo } from "@shared-types/location-info"
import { LockIcon } from "lucide-react"
import { useMemo } from "react"

export function AnchoredLocationForecastPanel() {
	return (
		<AnchoredPanelShell
			match={data => data?.type === "location" ? data : null}
			fallbackTitle={content => content.locationInfo.name}
			accentBorder="border-cyan-400/20"
			headerVariant="title"
			bodyClassName="px-3 pb-2.5 space-y-1.5"
		>
			{(content, ctx) => (
				<LocationBody
					locationInfo={content.locationInfo}
					countryName={content.countryName}
					ctx={ctx}
				/>
			)}
		</AnchoredPanelShell>
	)
}

interface LocationBodyProps {
	locationInfo: LocationInfo
	countryName: string | null
	ctx: PanelShellContext
}

function LocationBody({ locationInfo, countryName, ctx }: LocationBodyProps) {
	const dispatch = useAppDispatch()
	const userProfile = useAppSelector(state => state.user.userProfile)
	const { isPaid } = useEntitlements()

	const { data: currentWeather, isLoading: isLoadingCurrent, isError: isErrorCurrent } = useCurrentWeatherGrid(locationInfo.lat, locationInfo.lon)
	// Always fetch the full depth: free users see days 4+ as a blurred teaser
	// behind the upgrade CTA (client-side gate per SUBSCRIPTION_PRICING_MODEL.md)
	const { data: forecastData, isLoading: isLoadingForecast, isError: isErrorForecast } = useDailyForecastGrid(locationInfo.lat, locationInfo.lon, cPaidForecastDays)
	const { data: hourlyData } = useHourlyForecastGrid(locationInfo.lat, locationInfo.lon, 48)
	const { data: airQuality } = useCurrentAirQuality(locationInfo.lat, locationInfo.lon)

	const current = currentWeather?.current
	const daily = useMemo(
		() => (forecastData?.daily ?? []).slice(0, isPaid ? cPaidForecastDays : 7),
		[forecastData, isPaid]
	)
	const hourly = hourlyData?.hourly ?? []

	// Checkout needs an account: signed-out users register first
	const handleUnlock = () => {
		dispatch(userProfile ? setSubscribeOpen(true) : setAuthOpen(true))
	}

	return (
		<>
			<div className="flex items-center gap-1.5 text-xs text-slate-300">
				<img
					src={`${Constants.baseFlagUrl}/${locationInfo.country.toLowerCase()}.svg`}
					alt={countryName ?? locationInfo.country}
					className="w-4 h-3 object-cover rounded-sm"
				/>
				<span>{countryName ?? locationInfo.country}</span>
				{ctx.subtitle && (
					<span className="text-slate-400 truncate">{ctx.subtitle}</span>
				)}
				{locationInfo.pop > 0 && (
					<span className="text-slate-500">
						Pop. {locationInfo.pop.toLocaleString()}
					</span>
				)}
			</div>

			<div className="rounded-md border border-white/10 bg-white/[0.04] px-2 py-1.5">
				{isLoadingCurrent ? (
					<div className="text-xs text-slate-400">Loading...</div>
				) : isErrorCurrent ? (
					<div className="text-xs text-red-400">Weather unavailable</div>
				) : (
					<>
						<div className="flex items-center gap-2">
							{current?.weather?.code && (
								<img
									src={`/images/weather/${getWeatherIcon(current.weather.code)}.png`}
									alt={current.weather.description ?? ""}
									className="w-8 h-8"
								/>
							)}
							<div className="text-xl leading-none font-light text-white">
								{current?.temperature?.value != null ? `${Math.round(convertTemperature(current.temperature.value, 0))}°` : "--°"}
							</div>
							<div className="text-xs text-slate-300 flex-1 truncate">
								{current?.weather?.description ?? "--"}
							</div>
							<div className="text-right text-[11px] text-slate-400 leading-tight shrink-0">
								<div>
									{current?.wind?.speed != null
										? `${formatSpeed(current.wind.speed)} ${current.wind.cardinal ?? ""}`
										: "--"}
								</div>
								{current?.wind?.gust != null && (current.wind.speed == null || current.wind.gust > current.wind.speed + 1) && (
									<div className="text-slate-500">
										gusts {formatSpeed(current.wind.gust)}
									</div>
								)}
							</div>
						</div>
						{current && <CurrentConditionStats current={current} />}
						{airQuality && <AirQualityRow aq={airQuality} />}
					</>
				)}
			</div>

			<MoreSection compact={ctx.compact}>
				{hourly.length >= 2 && (
					<div className="space-y-1">
						<div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-400">
							48-Hour Forecast
						</div>
						<div className="pointer-events-auto overflow-x-auto pb-0.5 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent]">
							<HourlyTempCurve hourly={hourly} />
						</div>
					</div>
				)}

				<div className="space-y-1">
					<div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-400">
						{isPaid ? `${Math.max(daily.length, 7)}-Day Forecast` : "7-Day Forecast"}
					</div>
					{isLoadingForecast ? (
						<div className="text-xs text-slate-400">Loading forecast...</div>
					) : isErrorForecast ? (
						<div className="text-xs text-red-400">Unable to load forecast</div>
					) : (
						<DailyForecastGrid daily={daily} isPaid={isPaid} onUnlock={handleUnlock} />
					)}
				</div>
			</MoreSection>
		</>
	)
}

interface DailyForecastGridProps {
	daily: DailyForecast[]
	isPaid: boolean
	onUnlock: () => void
}

/// Daily forecast cells with the Phase 4 depth gate: free users get the
/// first cFreeForecastDays days, the remainder render blurred behind a
/// single lock overlay that opens the subscribe flow. Paid users get every
/// available day (up to cPaidForecastDays), horizontally scrollable past 7.
function DailyForecastGrid({ daily, isPaid, onUnlock }: DailyForecastGridProps) {
	if (isPaid) {
		const dayCount = Math.max(daily.length, 7)
		if (dayCount <= 7) {
			return (
				<div className="grid grid-cols-7 gap-0.5">
					{Array.from({ length: dayCount }, (_, i) => <DayCell key={i} day={daily[i]} />)}
				</div>
			)
		}
		return (
			<div className="pointer-events-auto overflow-x-auto pb-0.5 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent]">
				<div className="grid grid-flow-col auto-cols-[minmax(34px,1fr)] gap-0.5">
					{Array.from({ length: dayCount }, (_, i) => <DayCell key={i} day={daily[i]} />)}
				</div>
			</div>
		)
	}

	// Free: 3 unlocked days, days 4-7 blurred behind one lock overlay
	const lockedCount = 7 - cFreeForecastDays
	return (
		<div className="grid grid-cols-7 gap-0.5">
			{Array.from({ length: cFreeForecastDays }, (_, i) => <DayCell key={i} day={daily[i]} />)}
			<button
				type="button"
				onClick={onUnlock}
				className="relative col-span-4 pointer-events-auto cursor-pointer text-left"
				aria-label="Unlock the full forecast"
			>
				<div
					className="grid gap-0.5 blur-[3px] opacity-60 select-none"
					style={{ gridTemplateColumns: `repeat(${lockedCount}, minmax(0, 1fr))` }}
					aria-hidden
				>
					{Array.from({ length: lockedCount }, (_, i) => (
						<DayCell key={i} day={daily[cFreeForecastDays + i]} />
					))}
				</div>
				<div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 rounded-md">
					<LockIcon className="w-3.5 h-3.5 text-cyan-300" />
					<span className="text-[9px] font-medium text-cyan-300">Unlock {cPaidForecastDays} days</span>
				</div>
			</button>
		</div>
	)
}

function DayCell({ day }: { day: DailyForecast | undefined }) {
	const date = day?.date ? new Date(day.date + "T00:00:00") : null
	const dayName = date?.toLocaleDateString("en-US", { weekday: "short" }) ?? "---"
	const iconName = day?.weather?.code ? getWeatherIcon(day.weather.code) : "partly_cloudy"

	return (
		<div className="flex flex-col items-center rounded-md bg-white/[0.03] border border-white/5 px-0.5 py-1">
			<span className="text-[10px] text-slate-400">{dayName}</span>
			<img
				src={`/images/weather/${iconName}.png`}
				alt={day?.weather?.description ?? ""}
				className="w-6 h-6 mt-0.5"
			/>
			<span className="text-[11px] font-medium text-white">
				{day?.temperature?.max != null ? `${Math.round(convertTemperature(day.temperature.max, 0))}°` : "--"}
			</span>
			<span className="text-[10px] text-slate-500">
				{day?.temperature?.min != null ? `${Math.round(convertTemperature(day.temperature.min, 0))}°` : "--"}
			</span>
			<span className="text-[9px] text-cyan-300 min-h-[12px]">
				{day?.precipitation?.probability != null && day.precipitation.probability >= 10
					? `${day.precipitation.probability}%`
					: ""}
			</span>
		</div>
	)
}

function CurrentConditionStats({ current }: { current: CurrentWeatherResponse["current"] }) {
	const stats: Array<{ label: string, value: string }> = []

	if (current.temperature?.feels_like != null) {
		stats.push({ label: "Feels", value: `${Math.round(convertTemperature(current.temperature.feels_like, 0))}°` })
	}
	if (current.humidity != null) {
		stats.push({ label: "Humidity", value: `${current.humidity}%` })
	}
	if (current.dewpoint != null) {
		stats.push({ label: "Dew Pt", value: `${Math.round(convertTemperature(current.dewpoint, 0))}°` })
	}
	if (current.pressure?.value != null) {
		const trend = current.pressure.trend_3h
		const arrow = trend == null ? "" : trend >= 0.5 ? " ↑" : trend <= -0.5 ? " ↓" : " →"
		stats.push({ label: "hPa", value: `${Math.round(current.pressure.value)}${arrow}` })
	}
	if (current.clouds?.cover != null) {
		stats.push({ label: "Clouds", value: `${current.clouds.cover}%` })
	}
	if (current.precipitation?.last_hour != null && current.precipitation.last_hour > 0) {
		stats.push({ label: "Precip", value: formatPrecipitation(current.precipitation.last_hour) })
	}

	if (stats.length === 0) return null

	return (
		<div className="grid grid-cols-4 gap-x-1 gap-y-1 mt-1.5 pt-1.5 border-t border-white/5">
			{stats.map(stat => (
				<div key={stat.label} className="flex flex-col items-center">
					<span className="text-[11px] font-medium text-white leading-tight">{stat.value}</span>
					<span className="text-[9px] text-slate-400 leading-tight">{stat.label}</span>
				</div>
			))}
		</div>
	)
}

function AirQualityRow({ aq }: { aq: AirQualityInfo }) {
	return (
		<div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-white/5">
			<span
				className="w-2 h-2 rounded-full shrink-0"
				style={{ backgroundColor: aq.color }}
			/>
			<span className="text-[11px] font-medium text-white leading-tight">
				AQI {aq.aqi}
			</span>
			<span className="text-[11px] leading-tight truncate" style={{ color: aq.color }}>
				{aq.categoryLabel}
			</span>
			<span className="text-[10px] text-slate-500 leading-tight ml-auto shrink-0">
				{aq.dominantLabel}
			</span>
		</div>
	)
}

const CURVE_COL_WIDTH = 36
const CURVE_HEIGHT = 64
const CURVE_TOP_PAD = 16
const CURVE_BOTTOM_PAD = 14
const PRECIP_BAR_MAX_MM = 5

function HourlyTempCurve({ hourly }: { hourly: HourlyForecast[] }) {
	const width = hourly.length * CURVE_COL_WIDTH

	const temps = hourly.map(h => Math.round(convertTemperature(h.temperature, 0)))
	const minTemp = Math.min(...temps)
	const maxTemp = Math.max(...temps)
	const range = Math.max(maxTemp - minTemp, 1)

	const xFor = (i: number) => i * CURVE_COL_WIDTH + CURVE_COL_WIDTH / 2
	const yFor = (temp: number) =>
		CURVE_TOP_PAD + (1 - (temp - minTemp) / range) * (CURVE_HEIGHT - CURVE_TOP_PAD - CURVE_BOTTOM_PAD)

	const points = temps.map((t, i) => ({ x: xFor(i), y: yFor(t) }))
	const curvePath = smoothCurvePath(points)
	const fillPath = `${curvePath} L ${points[points.length - 1].x} ${CURVE_HEIGHT} L ${points[0].x} ${CURVE_HEIGHT} Z`

	// Show a condition icon at the first point and wherever the condition changes
	const showIcon = (i: number) => i === 0 || hourly[i].weather.code !== hourly[i - 1].weather.code

	return (
		<div className="relative" style={{ width }}>
			<div className="relative h-5" style={{ width }}>
				{hourly.map((hour, i) =>
					showIcon(i) ? (
						<img
							key={i}
							src={`/images/weather/${getWeatherIcon(hour.weather.code)}.png`}
							alt={hour.weather.description}
							className="absolute top-0 w-4 h-4"
							style={{ left: xFor(i) - 8 }}
						/>
					) : null
				)}
			</div>

			<svg width={width} height={CURVE_HEIGHT} className="block">
				<defs>
					<linearGradient id="tempCurveFill" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="#fcd34d" stopOpacity="0.25" />
						<stop offset="100%" stopColor="#fcd34d" stopOpacity="0" />
					</linearGradient>
				</defs>

				<path d={fillPath} fill="url(#tempCurveFill)" />
				<path d={curvePath} fill="none" stroke="#fcd34d" strokeWidth="1.5" strokeLinecap="round" />

				{hourly.map((hour, i) => {
					const precip = hour.precipitation_total
					if (precip == null || precip <= 0.05) return null
					const barHeight = Math.max(2, Math.min(1, precip / PRECIP_BAR_MAX_MM) * (CURVE_BOTTOM_PAD - 2))
					return (
						<rect
							key={i}
							x={xFor(i) - 5}
							y={CURVE_HEIGHT - barHeight}
							width={10}
							height={barHeight}
							rx={1.5}
							fill="#22d3ee"
							fillOpacity={0.7}
						>
							<title>{`Precipitation: ${formatPrecipitation(precip)}`}</title>
						</rect>
					)
				})}

				{points.map((point, i) => (
					<text
						key={i}
						x={point.x}
						y={point.y - 5}
						textAnchor="middle"
						fill="#ffffff"
						fontSize="9"
						fontWeight="500"
					>
						{temps[i]}°
					</text>
				))}
			</svg>

			<div className="flex" style={{ width }}>
				{hourly.map((hour, i) => {
					const precip = hour.precipitation_total
					return (
						<div key={i} className="flex flex-col items-center shrink-0" style={{ width: CURVE_COL_WIDTH }}>
							<span className="text-[9px] text-slate-400">{formatHourLabel(hour.time)}</span>
							<span
								className="flex items-center gap-[1px] text-[9px] text-cyan-300 min-h-[12px]"
								title={precip != null && precip > 0.1 ? `Precipitation: ${formatPrecipitation(precip)}` : undefined}
							>
								{precip != null && precip > 0.1 && (
									<>
										<svg width="5" height="7" viewBox="0 0 5 7" fill="currentColor" className="shrink-0">
											<path d="M2.5 0C2.5 0 0 3.1 0 4.6a2.5 2.4 0 0 0 5 0C5 3.1 2.5 0 2.5 0Z" />
										</svg>
										{formatPrecipCompact(precip)}
									</>
								)}
							</span>
						</div>
					)
				})}
			</div>
		</div>
	)
}

/** Catmull-Rom spline through the points, converted to cubic beziers */
function smoothCurvePath(points: Array<{ x: number, y: number }>): string {
	if (points.length < 2) return ""

	const round = (n: number) => Math.round(n * 10) / 10
	let path = `M ${round(points[0].x)} ${round(points[0].y)}`

	for (let i = 0; i < points.length - 1; i++) {
		const p0 = points[Math.max(0, i - 1)]
		const p1 = points[i]
		const p2 = points[i + 1]
		const p3 = points[Math.min(points.length - 1, i + 2)]

		const c1x = p1.x + (p2.x - p0.x) / 6
		const c1y = p1.y + (p2.y - p0.y) / 6
		const c2x = p2.x - (p3.x - p1.x) / 6
		const c2y = p2.y - (p3.y - p1.y) / 6

		path += ` C ${round(c1x)} ${round(c1y)}, ${round(c2x)} ${round(c2y)}, ${round(p2.x)} ${round(p2.y)}`
	}

	return path
}

/** Compact precip amount for the narrow curve columns, e.g. 0.13″ or 0.8mm */
function formatPrecipCompact(mm: number): string {
	const unit = getPrecipitationUnit()
	return unit === "in" ? `${convertPrecipitation(mm)}″` : `${convertPrecipitation(mm)}mm`
}

function formatHourLabel(isoTime: string): string {
	const date = new Date(isoTime)
	return date
		.toLocaleTimeString("en-US", { hour: "numeric" })
		.replace(" ", "")
		.toLowerCase()
}

/** Weather-code → icon filename (also used by the placemark conditions row) */
export function getWeatherIcon(code: string): string {
	const codeMap: Record<string, string> = {
		clear: "sunny",
		mostly_clear: "mostly_sunny",
		sunny: "sunny",
		mostly_sunny: "mostly_sunny",
		partly_cloudy: "partly_cloudy",
		mostly_cloudy: "mostly_cloudy",
		cloudy: "cloudy",
		overcast: "cloudy",
		fog: "fog",
		mist: "fog",
		haze: "haze",
		rain: "rain",
		light_rain: "showers",
		heavy_rain: "heavy_rain",
		drizzle: "showers",
		thunderstorm: "thunderstorms",
		thunderstorms: "thunderstorms",
		snow: "light_snow",
		light_snow: "light_snow",
		heavy_snow: "heavy_snow",
		sleet: "icy_mix",
		freezing_rain: "freezing_rain",
		hail: "hail"
	}

	return codeMap[code] ?? "partly_cloudy"
}
